import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast, Toaster } from "sonner";
import {
  ShieldCheck,
  Download,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  MessageCircle,
  Loader2,
  Users,
  Ban,
  AlertTriangle,
  Wallet,
  Lock,
  HelpCircle,
  Calendar,
  FileCheck2,
  RefreshCw,
} from "lucide-react";

/* Progress steps */
const STEPS = [
  { key: "profile", label: "Merchant profile" },
  { key: "kfs", label: "KFS" },
  { key: "cover", label: "Cover options" },
  { key: "payment", label: "Payment" },
  { key: "dashboard", label: "Dashboard" },
];

function ProgressIndicator({ active }) {
  const activeIdx = STEPS.findIndex((s) => s.key === active);
  return (
    <ol
      className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1"
      data-testid="kfs-progress"
    >
      {STEPS.map((s, i) => {
        const done = i < activeIdx;
        const current = i === activeIdx;
        return (
          <li key={s.key} className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span
              className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold transition-colors ${
                done
                  ? "bg-[#2d7a4a] text-white"
                  : current
                    ? "bg-[#114232] text-[#f4f2eb]"
                    : "bg-[#114232]/8 text-[#114232]"
              }`}
            >
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span
              className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${
                current
                  ? "text-[#114232]"
                  : done
                    ? "text-[#5c6360]"
                    : "text-[#5c6360]/70"
              }`}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <span className="w-4 sm:w-8 h-px bg-[#114232]/15" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function SectionCard({ icon: Icon, title, children, testid, tone }) {
  const isWarning = tone === "warning";
  const isDanger = tone === "danger";
  return (
    <div
      className={`rounded-2xl bg-white border p-6 sm:p-7 transition-colors ${
        isDanger
          ? "border-[#c15337]/25 bg-[#c15337]/4"
          : isWarning
            ? "border-[#d49a36]/25 bg-[#d49a36]/4"
            : "border-[#114232]/10 hover:border-[#114232]/20"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
            isDanger
              ? "bg-[#c15337]/15"
              : isWarning
                ? "bg-[#d49a36]/15"
                : "bg-[#114232]/8"
          }`}
        >
          <Icon
            className={`w-5 h-5 ${
              isDanger
                ? "text-[#c15337]"
                : isWarning
                  ? "text-[#8a6520]"
                  : "text-[#114232]"
            }`}
            strokeWidth={1.7}
          />
        </div>
        <h2 className="font-heading text-lg sm:text-xl font-bold text-[#114232]">
          {title}
        </h2>
      </div>
      <div className="text-sm sm:text-base text-[#5c6360] leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );
}

function Bullets({ items, accent }) {
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span
            className={`w-1.5 h-1.5 rounded-full mt-2.5 shrink-0 ${
              accent === "danger" ? "bg-[#c15337]" : "bg-[#2d7a4a]"
            }`}
          />
          <span className="text-sm sm:text-base text-[#1b1d1c]">{it}</span>
        </li>
      ))}
    </ul>
  );
}

const COPY = {
  en: {
    eyebrow: "Disclosure · Key Facts Statement",
    title: "Fortiqa Merchant Cyber Cover",
    intro:
      "Please read this Key Facts Summary carefully before continuing. It tells you what's covered, what's not, and what you agree to when you accept cover.",
    sections: {
      who: {
        title: "Who this cover is for",
        body: "Kenyan merchants accepting digital payments via M-Pesa, POS, bank transfer, or social-commerce platforms. Whether you're a market vendor, online seller, POS agent, or growing SME — Fortiqa is built for you.",
      },
      covered: {
        title: "What's covered",
        items: [
          "M-Pesa reversal fraud after goods or services have been delivered",
          "Phishing and account takeover loss on linked merchant tills",
          "POS settlement failures and clearing-account fraud",
          "Fake-customer disputes and chargeback fraud on legitimate sales",
          "Social-commerce scams (WhatsApp catalogue / Instagram shop impersonation)",
          "Delivery vs. payment mismatch on completed orders",
        ],
      },
      notCovered: {
        title: "What's not covered",
        items: [
          "Losses that occur during the 7-day cooling-off period",
          "Fraud committed by you or anyone with your explicit consent",
          "Claims without verifiable transaction evidence (M-Pesa SMS, screenshots, etc.)",
          "Pre-existing disputes raised before policy activation",
          "Losses arising from gross negligence (e.g. shared PINs, public credentials)",
          "Cover above your tier's per-claim limit",
        ],
      },
      eligibility: {
        title: "Eligibility",
        items: [
          "Active M-Pesa till, POS, or digital-payment channel",
          "Valid Kenyan ID or business registration document",
          "Verified phone number for WhatsApp claims",
          "Pass tiered KYC (selfie + ID + one business document)",
          "Active weekly premium payment in good standing",
        ],
      },
      cost: {
        title: "Cost",
        items: [
          "Starter — KES 99 / week · cover up to KES 50,000 per claim",
          "Growth — KES 249 / week · cover up to KES 250,000 per claim",
          "Business — KES 499 / week · cover up to KES 1,000,000 per claim",
          "Billed weekly via M-Pesa. No annual lock-in. Pause anytime.",
        ],
      },
      claims: {
        title: "Claims process",
        items: [
          "Submit on WhatsApp with screenshots, transaction ID, and a short description",
          "We acknowledge within seconds and open a FlashClaim ticket",
          "AI + OCR review the evidence in real-time (clean claims auto-approve)",
          "Edge cases route to a human SIU officer within minutes",
          "Payout via M-Pesa B2C to the same till — usually < 90 seconds",
        ],
      },
      cancellation: {
        title: "Cancellation & cooling-off",
        body: "You can cancel any time from the dashboard or via WhatsApp — your weekly billing stops immediately. A 7-day cooling-off period applies on first activation: claims in this window aren't eligible. Re-activation is supported any time.",
      },
      privacy: {
        title: "Data & privacy",
        body: "We collect merchant identity, KYC documents, transaction references, and device/security data for fraud prevention, cover management, claims review, and IRA-aligned compliance. We never sell your data. You can request a copy or deletion at any time per the Kenya Data Protection Act, 2019.",
      },
      risks: {
        title: "Important risk warnings",
        items: [
          "Fortiqa is built for IRA Kenya regulatory alignment but is not a substitute for general business insurance.",
          "Cover starts only after the 7-day cooling-off window — fraud during this window is not eligible.",
          "Premium payments must remain current. A 3-day grace period applies before cover auto-suspends.",
          "Each claim is reviewed individually — auto-approval is not guaranteed.",
        ],
      },
    },
    contact: {
      title: "Questions before you accept?",
      body: "Our team is on WhatsApp and email. We'll walk you through anything in this document — in English or Swahili.",
    },
    cta: {
      acceptedHeading: "You've accepted the KFS",
      acceptedSub: "Version {version} accepted on {date}",
      accept: "Accept KFS",
      accepting: "Recording…",
      continue: "Continue to cover options",
      download: "Download PDF",
      back: "Back to dashboard",
      contact: "Contact support",
    },
  },
  sw: {
    eyebrow: "Maelezo · Mukhtasari Muhimu",
    title: "Bima ya Mtandao ya Wafanyabiashara — Fortiqa",
    intro:
      "Tafadhali soma mukhtasari huu kabla ya kuendelea. Unaeleza kile kinachofunikwa, kisichofunikwa, na unachokubali.",
    sections: {
      who: {
        title: "Nani anastahili bima hii",
        body: "Wafanyabiashara wa Kenya wanaopokea malipo ya kidijitali — M-Pesa, POS, akaunti za benki, au mitandao ya kijamii.",
      },
      covered: {
        title: "Yanayofunikwa",
        items: [
          "Udanganyifu wa kurudisha pesa M-Pesa baada ya kuwasilisha bidhaa",
          "Phishing na uvunjaji wa akaunti za till",
          "Hasara za malipo ya POS",
          "Madai bandia ya wateja",
          "Udanganyifu wa biashara za mtandao wa kijamii",
          "Hasara ya utoaji vs malipo",
        ],
      },
      notCovered: {
        title: "Yasiyofunikwa",
        items: [
          "Hasara wakati wa siku 7 za kupoa",
          "Udanganyifu uliofanywa na wewe au mtu uliyemruhusu",
          "Madai yasiyokuwa na ushahidi wa muamala",
          "Madai ya kabla ya kuanzisha bima",
          "Hasara kutokana na uzembe (k.m. kushiriki PIN)",
          "Hasara zaidi ya kiwango cha bima yako",
        ],
      },
      eligibility: {
        title: "Sifa za kustahili",
        items: [
          "Till hai ya M-Pesa, POS, au malipo ya kidijitali",
          "Kitambulisho cha Kenya au cheti cha biashara",
          "Nambari ya simu iliyothibitishwa",
          "Pitisha KYC (selfie + kitambulisho + hati moja ya biashara)",
          "Malipo ya kila wiki ya bima yakawa hai",
        ],
      },
      cost: {
        title: "Gharama",
        items: [
          "Starter — KES 99/wiki · hadi KES 50,000 kwa kila dai",
          "Growth — KES 249/wiki · hadi KES 250,000 kwa kila dai",
          "Business — KES 499/wiki · hadi KES 1,000,000 kwa kila dai",
          "Malipo kila wiki kupitia M-Pesa. Simamisha wakati wowote.",
        ],
      },
      claims: {
        title: "Jinsi ya kudai",
        items: [
          "Wasilisha kwa WhatsApp na ushahidi",
          "Tunakubali ndani ya sekunde",
          "AI inakagua ushahidi mara moja",
          "Madai magumu yanaenda kwa afisa wa SIU",
          "Malipo M-Pesa kwa till hiyo hiyo — kawaida < sekunde 90",
        ],
      },
      cancellation: {
        title: "Kufuta na kipindi cha kupoa",
        body: "Unaweza kufuta wakati wowote kupitia dashibodi au WhatsApp. Siku 7 za kupoa zinatumika baada ya kuanzisha bima — madai katika kipindi hiki hayakubaliki.",
      },
      privacy: {
        title: "Data na faragha",
        body: "Tunakusanya data ya mfanyabiashara, KYC, marejeleo ya miamala, na data ya kifaa kwa ajili ya kuzuia udanganyifu na utii wa sheria. Hatuuzi data yako. Unaweza kuomba nakala au kufuta wakati wowote.",
      },
      risks: {
        title: "Onyo muhimu",
        items: [
          "Fortiqa imejengwa kwa msingi wa IRA Kenya lakini si mbadala wa bima ya jumla ya biashara.",
          "Bima huanza baada ya siku 7 za kupoa.",
          "Malipo ya kila wiki yanapaswa kuwa hai.",
          "Kila dai linakaguliwa mmoja-mmoja.",
        ],
      },
    },
    contact: {
      title: "Una maswali?",
      body: "Timu yetu iko WhatsApp na barua pepe.",
    },
    cta: {
      acceptedHeading: "Umekubali KFS",
      acceptedSub: "Toleo {version} lilikubaliwa tarehe {date}",
      accept: "Kubali KFS",
      accepting: "Inarekodi…",
      continue: "Endelea",
      download: "Pakua PDF",
      back: "Rudi dashibodi",
      contact: "Wasiliana nasi",
    },
  },
};

export default function MerchantKFS() {
  const navigate = useNavigate();
  const [lang, setLang] = useState("en");
  const [status, setStatus] = useState(null); // { accepted, current_version, record }
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const t = COPY[lang] || COPY.en;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/merchant/kfs/status");
      setStatus(r.data);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(detail);
      if (e.response?.status === 401) {
        navigate("/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  async function accept() {
    setAccepting(true);
    try {
      const r = await api.post("/merchant/kfs/accept", { language: lang });
      if (r.data.already_accepted) {
        toast.success("Already on file — nothing to do.");
      } else {
        toast.success("KFS accepted. Thank you!");
      }
      await loadStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAccepting(false);
    }
  }

  async function downloadPdf() {
    setDownloading(true);
    try {
      const { data } = await api.get(`/pdfs/kfs/${lang}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fortiqa_kfs_${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Couldn't download PDF — please try again.",
      );
    } finally {
      setDownloading(false);
    }
  }

  const accepted = status?.accepted;
  const acceptedAt = status?.record?.accepted_at;
  const acceptedDate = acceptedAt ? new Date(acceptedAt).toLocaleDateString("en-KE") : "";

  return (
    <div className="px-5 sm:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8 max-w-5xl mx-auto" data-testid="merchant-kfs-page">
      <Toaster richColors position="top-right" />

      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#c15337]">
            {t.eyebrow}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#114232] mt-1.5 flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8" /> {t.title}
          </h1>
          <p className="text-sm sm:text-base text-[#5c6360] mt-2 max-w-2xl leading-relaxed">
            {t.intro}
          </p>
        </div>
        <div
          className="inline-flex border border-[#114232]/15 rounded-full overflow-hidden text-sm bg-white"
          data-testid="kfs-lang-switcher"
        >
          {[["en", "English"], ["sw", "Kiswahili"]].map(([code, label]) => (
            <button
              key={code}
              onClick={() => setLang(code)}
              data-testid={`kfs-lang-${code}`}
              className={`px-4 py-1.5 transition-colors ${
                lang === code
                  ? "bg-[#114232] text-[#f4f2eb]"
                  : "hover:bg-[#114232]/5 text-[#5c6360]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Progress */}
      <ProgressIndicator active="kfs" />

      {/* Status banner */}
      {loading ? (
        <div className="rounded-2xl bg-white border border-[#114232]/10 p-8 grid place-items-center text-[#5c6360]" data-testid="kfs-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-2xl bg-[#c15337]/8 border border-[#c15337]/25 p-5 flex items-start justify-between gap-4" data-testid="kfs-error">
          <div className="text-sm text-[#114232]">
            <strong>Couldn't load your KFS status.</strong>{" "}
            <span className="text-[#5c6360]">{error}</span>
          </div>
          <button
            onClick={loadStatus}
            data-testid="kfs-retry"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#114232]/20 rounded-full bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors"
          >
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
        </div>
      ) : accepted ? (
        <div
          className="rounded-2xl bg-[#2d7a4a]/8 border border-[#2d7a4a]/30 p-5 sm:p-6 flex items-start gap-4"
          data-testid="kfs-accepted-banner"
        >
          <CheckCircle2 className="w-6 h-6 text-[#2d7a4a] shrink-0 mt-0.5" strokeWidth={2} />
          <div className="flex-1">
            <div className="font-heading font-bold text-[#114232]">
              {t.cta.acceptedHeading}
            </div>
            <div className="text-sm text-[#5c6360] mt-0.5">
              {t.cta.acceptedSub
                .replace("{version}", status?.record?.version || status?.current_version)
                .replace("{date}", acceptedDate || "—")}
            </div>
          </div>
        </div>
      ) : (
        <div
          className="rounded-2xl bg-[#d49a36]/8 border border-[#d49a36]/30 p-5 sm:p-6 flex items-start gap-4"
          data-testid="kfs-pending-banner"
        >
          <AlertTriangle className="w-6 h-6 text-[#8a6520] shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <div className="font-heading font-bold text-[#114232]">
              Awaiting your acceptance
            </div>
            <div className="text-sm text-[#5c6360] mt-0.5">
              Please review the sections below, then tap{" "}
              <strong className="text-[#114232]">Accept KFS</strong> to continue.
            </div>
          </div>
        </div>
      )}

      {/* Sections grid */}
      <div className="grid sm:grid-cols-2 gap-5">
        <SectionCard icon={Users} title={t.sections.who.title} testid="kfs-section-who">
          <p>{t.sections.who.body}</p>
        </SectionCard>
        <SectionCard
          icon={ShieldCheck}
          title={t.sections.covered.title}
          testid="kfs-section-covered"
        >
          <Bullets items={t.sections.covered.items} />
        </SectionCard>
        <SectionCard
          icon={Ban}
          title={t.sections.notCovered.title}
          testid="kfs-section-not-covered"
          tone="warning"
        >
          <Bullets items={t.sections.notCovered.items} accent="danger" />
        </SectionCard>
        <SectionCard
          icon={FileCheck2}
          title={t.sections.eligibility.title}
          testid="kfs-section-eligibility"
        >
          <Bullets items={t.sections.eligibility.items} />
        </SectionCard>
        <SectionCard
          icon={Wallet}
          title={t.sections.cost.title}
          testid="kfs-section-cost"
        >
          <Bullets items={t.sections.cost.items} />
        </SectionCard>
        <SectionCard
          icon={Calendar}
          title={t.sections.claims.title}
          testid="kfs-section-claims"
        >
          <Bullets items={t.sections.claims.items} />
        </SectionCard>
        <SectionCard
          icon={RefreshCw}
          title={t.sections.cancellation.title}
          testid="kfs-section-cancellation"
        >
          <p>{t.sections.cancellation.body}</p>
        </SectionCard>
        <SectionCard
          icon={Lock}
          title={t.sections.privacy.title}
          testid="kfs-section-privacy"
        >
          <p>{t.sections.privacy.body}</p>
        </SectionCard>
      </div>

      {/* Risks (danger tone, full width) */}
      <SectionCard
        icon={AlertTriangle}
        title={t.sections.risks.title}
        testid="kfs-section-risks"
        tone="danger"
      >
        <Bullets items={t.sections.risks.items} accent="danger" />
      </SectionCard>

      {/* Contact */}
      <SectionCard icon={HelpCircle} title={t.contact.title} testid="kfs-section-contact">
        <p>{t.contact.body}</p>
        <div className="flex flex-wrap gap-2.5 mt-3">
          <a
            href="https://wa.me/254700000000?text=Hi%20Fortiqa%2C%20I%20have%20a%20question%20about%20the%20KFS"
            target="_blank"
            rel="noreferrer"
            data-testid="kfs-contact-whatsapp"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1ebd5b] text-white text-sm font-semibold transition-all"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
          <a
            href="mailto:support@fortiqa.co.ke"
            data-testid="kfs-contact-email"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#114232]/15 hover:border-[#114232]/30 text-[#114232] text-sm font-semibold transition-all"
          >
            support@fortiqa.co.ke
          </a>
        </div>
      </SectionCard>

      {/* Sticky CTA bar */}
      <div
        className="sticky bottom-4 z-20 bg-white border border-[#114232]/10 rounded-2xl shadow-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
        data-testid="kfs-cta-bar"
      >
        <Link
          to="/merchant"
          data-testid="kfs-back-dashboard"
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-[#5c6360] hover:text-[#114232] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {t.cta.back}
        </Link>
        <button
          onClick={downloadPdf}
          disabled={downloading}
          data-testid="kfs-download-pdf"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#114232]/15 hover:border-[#114232]/30 hover:bg-[#114232]/4 text-[#114232] text-sm font-semibold transition-all disabled:opacity-60"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {t.cta.download}
        </button>
        <div className="flex-1" />
        {accepted ? (
          <Link
            to="/merchant/cover-options"
            data-testid="kfs-continue"
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white text-sm font-semibold transition-all shadow-md hover:shadow-lg"
          >
            {t.cta.continue} <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <button
            onClick={accept}
            disabled={accepting || loading}
            data-testid="kfs-accept"
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-[#114232] hover:bg-[#0d3125] text-[#f4f2eb] text-sm font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {accepting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> {t.cta.accepting}
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" /> {t.cta.accept}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
